
import React, { useState, useEffect } from 'react';
import { useAiQ } from '../state/AiQState';
import { IconStamp, IconGlobe, IconSearch, IconSparkles } from '../components/Icons';
import { explainRegulatoryRejection } from '../services/geminiService';
import toast from 'react-hot-toast';

interface ReportEntry {
    id: string;
    tradeId: string;
    type: 'New Trade' | 'Valuation Update' | 'Termination';
    regulator: 'HKTR' | 'DTCC' | 'EMIR';
    status: 'Pending' | 'Accepted' | 'Rejected' | 'Sent';
    errorCode?: string;
    timestamp: string;
}

const RegulatoryView: React.FC = () => {
    const { trades } = useAiQ();
    const [reports, setReports] = useState<ReportEntry[]>([]);
    const [selectedReport, setSelectedReport] = useState<ReportEntry | null>(null);
    const [aiFixSuggestion, setAiFixSuggestion] = useState('');
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    // Simulate Report Generation from Trades
    useEffect(() => {
        const generatedReports: ReportEntry[] = trades.map((t, idx) => {
            // Randomly assign status for demo purposes
            const rand = Math.random();
            let status: ReportEntry['status'] = 'Accepted';
            let errorCode = undefined;

            if (rand > 0.9) {
                status = 'Rejected';
                errorCode = 'ERR-2004: LEI_MISSING_OR_INVALID';
            } else if (rand > 0.8) {
                status = 'Pending';
            } else if (rand > 0.7) {
                status = 'Sent';
            }

            return {
                id: `RPT-${1000 + idx}`,
                tradeId: t.id,
                type: 'New Trade',
                regulator: 'HKTR', // Hong Kong Trade Repository
                status,
                errorCode,
                timestamp: t.tradeDate
            };
        });
        setReports(generatedReports);
    }, [trades]);

    const handleAnalyzeRejection = async (report: ReportEntry) => {
        if (!report.errorCode) return;
        setIsAnalyzing(true);
        setAiFixSuggestion('');
        
        const relatedTrade = trades.find(t => t.id === report.tradeId);
        const explanation = await explainRegulatoryRejection(report.errorCode, relatedTrade || {});
        
        setAiFixSuggestion(explanation);
        setIsAnalyzing(false);
    };

    const handleRegenerate = () => {
        toast.success("XML Regenerated and Queued for Submission");
        if (selectedReport) {
            setReports(prev => prev.map(r => r.id === selectedReport.id ? { ...r, status: 'Sent', errorCode: undefined } : r));
            setSelectedReport(prev => prev ? { ...prev, status: 'Sent', errorCode: undefined } : null);
            setAiFixSuggestion('');
        }
    };

    return (
        <div className="flex h-full bg-slate-100">
            {/* Main Panel: Report Status */}
            <div className="flex-1 flex flex-col border-r border-slate-200 bg-white">
                <div className="p-6 border-b border-slate-100">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
                        <IconStamp /> Regulatory Reporting Hub
                    </h2>
                    <p className="text-xs text-slate-500">HKTR / EMIR / DTCC Compliance Monitor</p>
                    
                    <div className="grid grid-cols-4 gap-4 mt-6">
                        <div className="bg-slate-50 p-3 rounded border border-slate-200">
                            <div className="text-xs text-slate-500 uppercase font-bold">Submission Rate</div>
                            <div className="text-xl font-bold text-slate-800">94.2%</div>
                        </div>
                        <div className="bg-red-50 p-3 rounded border border-red-200">
                            <div className="text-xs text-red-500 uppercase font-bold">Rejections (NACK)</div>
                            <div className="text-xl font-bold text-red-700">{reports.filter(r => r.status === 'Rejected').length}</div>
                        </div>
                        <div className="bg-blue-50 p-3 rounded border border-blue-200">
                            <div className="text-xs text-blue-500 uppercase font-bold">Pending ACK</div>
                            <div className="text-xl font-bold text-blue-700">{reports.filter(r => r.status === 'Sent').length}</div>
                        </div>
                        <div className="bg-emerald-50 p-3 rounded border border-emerald-200">
                            <div className="text-xs text-emerald-500 uppercase font-bold">Accepted (ACK)</div>
                            <div className="text-xl font-bold text-emerald-700">{reports.filter(r => r.status === 'Accepted').length}</div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                <th className="px-4 py-3 font-medium">Report ID</th>
                                <th className="px-4 py-3 font-medium">Trade ID</th>
                                <th className="px-4 py-3 font-medium">Type</th>
                                <th className="px-4 py-3 font-medium">Regulator</th>
                                <th className="px-4 py-3 font-medium">Status</th>
                                <th className="px-4 py-3 font-medium">Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {reports.map(rpt => (
                                <tr 
                                    key={rpt.id} 
                                    onClick={() => setSelectedReport(rpt)}
                                    className={`cursor-pointer transition-colors ${selectedReport?.id === rpt.id ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                                >
                                    <td className="px-4 py-3 font-mono text-slate-600 text-xs">{rpt.id}</td>
                                    <td className="px-4 py-3 text-slate-800">{rpt.tradeId}</td>
                                    <td className="px-4 py-3 text-slate-600">{rpt.type}</td>
                                    <td className="px-4 py-3 text-slate-600 font-bold">{rpt.regulator}</td>
                                    <td className="px-4 py-3">
                                        {rpt.status === 'Rejected' && <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">REJECTED</span>}
                                        {rpt.status === 'Accepted' && <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-xs font-bold">ACCEPTED</span>}
                                        {rpt.status === 'Sent' && <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold">SENT</span>}
                                        {rpt.status === 'Pending' && <span className="bg-slate-200 text-slate-600 px-2 py-1 rounded text-xs font-bold">PENDING</span>}
                                    </td>
                                    <td className="px-4 py-3 text-slate-500 text-xs">{rpt.timestamp}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Right: Exception Handler */}
            <div className="w-96 bg-slate-50 flex flex-col border-l border-slate-200 overflow-y-auto custom-scrollbar">
                {!selectedReport ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-10 text-center">
                        <div className="opacity-20 mb-4 text-5xl"><IconGlobe /></div>
                        <p>Select a report to view XML details and validation status.</p>
                    </div>
                ) : (
                    <div className="p-6 space-y-6">
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <h3 className="text-lg font-bold text-slate-800">{selectedReport.id}</h3>
                                <span className="text-xs font-mono text-slate-400">{selectedReport.regulator}</span>
                            </div>
                            <p className="text-xs text-slate-500">Linked Trade: {selectedReport.tradeId}</p>
                        </div>

                        <div className="bg-white p-4 rounded border border-slate-200 shadow-sm">
                            <h4 className="text-xs font-bold text-slate-700 uppercase mb-3 border-b border-slate-100 pb-2">Validation Output</h4>
                            {selectedReport.status === 'Rejected' ? (
                                <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                                    <div className="flex items-center gap-2 text-red-700 font-bold text-sm mb-1">
                                        <span className="text-lg">⚠️</span> NACK Received
                                    </div>
                                    <code className="text-xs text-red-800 block font-mono break-all">
                                        {selectedReport.errorCode}
                                    </code>
                                </div>
                            ) : (
                                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-emerald-700 text-sm font-medium flex items-center gap-2">
                                    ✅ Validated Successfully
                                </div>
                            )}
                            
                            {/* XML Preview */}
                            <div className="bg-slate-900 rounded p-3 overflow-hidden">
                                <div className="text-[10px] text-slate-400 font-mono mb-1">PAYLOAD PREVIEW (XML)</div>
                                <pre className="text-[10px] text-green-400 font-mono overflow-x-auto">
{`<TradeReport>
  <Header>
    <MsgId>${selectedReport.id}</MsgId>
    <SentBy>AILABQUANT_HK</SentBy>
  </Header>
  <Trade>
    <TradeId>${selectedReport.tradeId}</TradeId>
    <ProductType>${selectedReport.type}</ProductType>
    <Notional>...</Notional>
  </Trade>
</TradeReport>`}
                                </pre>
                            </div>
                        </div>

                        {selectedReport.status === 'Rejected' && (
                            <div className="space-y-3 animate-fade-in">
                                <button 
                                    onClick={() => handleAnalyzeRejection(selectedReport)}
                                    disabled={isAnalyzing}
                                    className="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                                >
                                    {isAnalyzing ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : <IconSparkles />}
                                    Ask AiQ to Diagnose
                                </button>

                                {aiFixSuggestion && (
                                    <div className="bg-purple-50 border border-purple-100 rounded p-4 shadow-sm">
                                        <h5 className="text-purple-800 font-bold text-xs uppercase mb-2 flex items-center gap-1">
                                            <IconSparkles /> Fix Suggestion
                                        </h5>
                                        <p className="text-sm text-purple-900 leading-relaxed">
                                            {aiFixSuggestion}
                                        </p>
                                    </div>
                                )}

                                <button 
                                    onClick={handleRegenerate}
                                    className="w-full py-2 bg-slate-800 hover:bg-slate-900 text-white rounded text-sm font-medium transition-colors"
                                >
                                    Apply Fix & Resubmit
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RegulatoryView;
